/**
 * Formatting Utilities
 *
 * General-purpose text formatting functions used across the app.
 */

/**
 * Gets ordinal suffix for a number (st, nd, rd, th)
 *
 * @param n - The number
 * @returns Just the suffix: "st", "nd", "rd", or "th"
 *
 * @example
 * getOrdinalSuffix(1) // returns "st"
 * getOrdinalSuffix(2) // returns "nd"
 * getOrdinalSuffix(3) // returns "rd"
 * getOrdinalSuffix(11) // returns "th"
 * getOrdinalSuffix(21) // returns "st"
 */
export function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

/**
 * Formats a number with its ordinal suffix (1st, 2nd, 3rd, etc.)
 *
 * @param n - The number
 * @returns Full ordinal string: "1st", "2nd", "3rd", "4th", etc.
 *
 * @example
 * formatOrdinal(1) // returns "1st"
 * formatOrdinal(22) // returns "22nd"
 * formatOrdinal(103) // returns "103rd"
 */
export function formatOrdinal(n: number): string {
  return `${n}${getOrdinalSuffix(n)}`;
}
